import { useCallback, useEffect } from "react";
import { ContractorUser, CreateContractorUser, UpdateContractorUser } from "../types/models/ContractorUser.d";
import { UpdateEntityContractorUser } from "../types/models/ContractorUser";
import { useGetContractorUserListQuery, useInsertContractorUserMutation, useUpdateContractorUserMutation, useDeleteContractorUserMutation, } from "../apis/ContractorUser.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useContractorUser(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertContractorUserMutation, { 
        isLoading: insertContractorUserLoading, 
        isSuccess: insertContractorUserIsSuccess,
        isError: insertContractorUserIsError,
        error: insertContractorUserError,
    }] = useInsertContractorUserMutation()
    const [updateContractorUserMutation, { 
        isLoading: updateContractorUserLoading,
        isSuccess: updateContractorUserIsSuccess,
        isError: updateContractorUserIsError,
        error: updateContractorUserError,
    }] = useUpdateContractorUserMutation()
    const [deleteContractorUserMutation, { 
        isLoading: deleteContractorUserLoading,
        isSuccess: deleteContractorUserIsSuccess,
        isError: deleteContractorUserIsError,
        error: deleteContractorUserError,
    }] = useDeleteContractorUserMutation()

    const loading =
        insertContractorUserLoading ||
        updateContractorUserLoading ||
        deleteContractorUserLoading ||
        false

    const insertContractorUser = useCallback(async (contractorUser: CreateContractorUser, redirectPath: string|undefined) => {
        try {
            const data = await insertContractorUserMutation(contractorUser).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertContractorUserMutation, navigate])
    
    useEffect(() => {
        if (insertContractorUserIsSuccess) {
            openSuccessAlert('Contractor User created')
        } else if (insertContractorUserIsError) {
            console.log("add error", insertContractorUserError)
            openFailAlert(t`Contractor User add error: ` + insertContractorUserError?.data?.message)
        }
    }, [
        insertContractorUserIsSuccess,
        insertContractorUserIsError,
        insertContractorUserError
    ]);

    const updateContractorUser = useCallback(async (contractorUser: UpdateEntityContractorUser, redirectPath: string|undefined) => {
        try {
            const data = await updateContractorUserMutation(contractorUser).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateContractorUserMutation, navigate])
    
    useEffect(() => {
        if (updateContractorUserIsSuccess) {
            openSuccessAlert(t`Contractor User updated`)
        } else if (updateContractorUserIsError) {
            console.log("update error", updateContractorUserError)
            openFailAlert(t`Contractor User update error: ` + updateContractorUserError?.data?.message)
        }
    }, [
        updateContractorUserIsSuccess,
        updateContractorUserIsError,
        updateContractorUserError
    ]);

    const deleteContractorUser = useCallback((contractorUser: ContractorUser, redirectPath: string|undefined) => {
        openDialog(async () => {
            contractorUser = {
                ...contractorUser,
            }
            try {
                const { data } = await deleteContractorUserMutation(contractorUser);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteContractorUserMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteContractorUserIsSuccess) {
            openSuccessAlert(t`Contractor User deleted`)
        } else if (deleteContractorUserIsError) {
            console.log("delete error", deleteContractorUserError)
            openFailAlert(t`Contractor User delete error: ` + deleteContractorUserError?.data?.message)
        }
    }, [
        deleteContractorUserIsSuccess,
        deleteContractorUserIsError,
        deleteContractorUserError
    ]);

    return {
        loading,
        insertContractorUser,
        updateContractorUser,
        deleteContractorUser
    }
}
